import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import API, { formatPrice } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { Receipt, ChevronRight, Wallet } from "lucide-react";

const paymentStatusMap = {
  unpaid: { id: "Belum Bayar", en: "Unpaid", color: "bg-amber-100 text-amber-800" },
  partially_paid: { id: "Bayar Sebagian", en: "Partially Paid", color: "bg-blue-100 text-blue-800" },
  dp_paid: { id: "DP Lunas", en: "Deposit Paid", color: "bg-sky-100 text-sky-800" },
  paid: { id: "Lunas", en: "Paid", color: "bg-emerald-100 text-emerald-800" },
  cancelled: { id: "Dibatalkan", en: "Cancelled", color: "bg-red-100 text-red-800" },
};

const orderProgressMap = {
  pending: { id: "Menunggu Diproses", en: "Pending", color: "bg-stone-100 text-stone-700" },
  waiting_po_close: { id: "Menunggu PO Close", en: "Waiting PO Close", color: "bg-yellow-100 text-yellow-800" },
  po_closed: { id: "PO Ditutup", en: "PO Closed", color: "bg-orange-100 text-orange-800" },
  in_production: { id: "Sedang Produksi", en: "In Production", color: "bg-indigo-100 text-indigo-800" },
  ready_at_central: { id: "Ready di Pusat", en: "Ready at Central", color: "bg-violet-100 text-violet-800" },
  ready_at_gallery: { id: "Ready di Gallery", en: "Ready at Gallery", color: "bg-fuchsia-100 text-fuchsia-800" },
  ready_to_ship: { id: "Siap Kirim", en: "Ready to Ship", color: "bg-cyan-100 text-cyan-800" },
  shipped: { id: "Dikirim", en: "Shipped", color: "bg-purple-100 text-purple-800" },
  completed: { id: "Selesai", en: "Completed", color: "bg-emerald-600 text-white" },
  cancelled: { id: "Dibatalkan", en: "Cancelled", color: "bg-red-100 text-red-800" },
};

export default function BillingPage() {
  const { locale } = useLanguage();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [waitingOrders, setWaitingOrders] = useState([]);

  useEffect(() => {
    const fetchBilling = async () => {
      try {
        setLoading(true);
        const res = await API.get("/billing/summary");
        setSummary(res.data.summary || null);
        setOrders(res.data.orders || []);
        setWaitingOrders(res.data.waiting_orders || []);
      } catch {
        setSummary(null);
        setOrders([]);
        setWaitingOrders([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBilling();
  }, []);

  const getPaymentMeta = (code) => paymentStatusMap[code] || { id: code || "-", en: code || "-", color: "bg-stone-100 text-stone-700" };
  const getOrderMeta = (code) => orderProgressMap[code] || { id: code || "-", en: code || "-", color: "bg-stone-100 text-stone-700" };
  const view = searchParams.get("view") === "waiting" ? "waiting" : "active";
  const activeOrders = view === "waiting" ? waitingOrders : orders;
  const pageTitle = view === "waiting" ? "Proses" : "Tagihan";
  const pageDesc = view === "waiting"
    ? "Daftar pesanan yang belum lunas, tetapi masih menunggu pesanan selesai / ready."
    : "Ringkasan tagihan DP dan pelunasan per transaksi.";

  return (
    <div className="min-h-screen bg-stone-50" data-testid="billing-page">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl text-stone-900">{pageTitle}</h1>
            <p className="text-sm text-stone-500 mt-1">{pageDesc}</p>
          </div>
          <Link to="/orders" className="text-sm text-stone-600 hover:text-stone-900 transition-colors">← Kembali ke My Orders</Link>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map((i) => <div key={i} className="h-28 bg-white border border-stone-200 rounded-xl animate-pulse" />)}
          </div>
        ) : !summary ? (
          <div className="bg-white border border-dashed border-stone-200 rounded-xl p-10 text-center">
            <Receipt className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-500">Data tagihan belum tersedia.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
              <div className="bg-white border border-stone-200 rounded-xl p-5">
                <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Total Outstanding</p>
                <p className="text-2xl font-serif text-amber-700 mt-2">{formatPrice(summary.outstanding_total || 0)}</p>
              </div>
              <div className="bg-white border border-stone-200 rounded-xl p-5">
                <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Tagihan DP</p>
                <p className="text-2xl font-serif text-sky-700 mt-2">{formatPrice(summary.dp_due_total || 0)}</p>
              </div>
              <div className="bg-white border border-stone-200 rounded-xl p-5">
                <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Tagihan Pelunasan</p>
                <p className="text-2xl font-serif text-violet-700 mt-2">{formatPrice(summary.settlement_due_total || 0)}</p>
              </div>
              <div className="bg-white border border-stone-200 rounded-xl p-5">
                <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Tagihan Aktif</p>
                <p className="text-2xl font-serif text-stone-900 mt-2">{summary.outstanding_orders || 0}</p>
                <p className="text-xs text-stone-500 mt-1">dari {summary.total_orders || 0} transaksi</p>
              </div>
            </div>

            {activeOrders.length === 0 ? (
              <div className="bg-white border border-dashed border-stone-200 rounded-xl p-10 text-center">
                <Wallet className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">{view === "waiting" ? "Saat ini tidak ada pesanan proses yang menunggu selesai." : "Saat ini tidak ada tagihan aktif."}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {activeOrders.map((order) => {
                  const paymentMeta = getPaymentMeta(order.payment_status_v2);
                  const orderMeta = getOrderMeta(order.order_status_v2);
                  const ps = order.payment_summary || {};
                  return (
                    <div key={order.transaction_id} className="bg-white border border-stone-200 rounded-xl p-5 shadow-sm">
                      <div className="flex flex-col lg:flex-row justify-between gap-4">
                        <div className="space-y-2 flex-1 min-w-0">
                          <div>
                            <p className="font-semibold text-stone-900 break-all">{order.transaction_id}</p>
                            <p className="text-xs text-stone-500 mt-1">{new Date(order.transaction_date).toLocaleString(locale === "id" ? "id-ID" : "en-US")}</p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Badge className={`${paymentMeta.color} text-[10px] uppercase tracking-wider font-semibold py-1 px-2 rounded-md border-0`}>
                              Bayar: {locale === "id" ? paymentMeta.id : paymentMeta.en}
                            </Badge>
                            <Badge className={`${orderMeta.color} text-[10px] uppercase tracking-wider font-semibold py-1 px-2 rounded-md border-0`}>
                              Pesanan: {locale === "id" ? orderMeta.id : orderMeta.en}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-stone-600 pt-2">
                            <div>
                              <p className="text-[11px] uppercase tracking-wider text-stone-400">Total</p>
                              <p className="font-semibold text-stone-900">{formatPrice(ps.grand_total || 0)}</p>
                            </div>
                            <div>
                              <p className="text-[11px] uppercase tracking-wider text-stone-400">Sudah Dibayar</p>
                              <p className="font-semibold text-emerald-700">{formatPrice(ps.total_paid || 0)}</p>
                            </div>
                            <div>
                              <p className="text-[11px] uppercase tracking-wider text-stone-400">Sisa</p>
                              <p className="font-semibold text-amber-700">{formatPrice(ps.outstanding_total || 0)}</p>
                            </div>
                            <div>
                              <p className="text-[11px] uppercase tracking-wider text-stone-400">Tahap Pembayaran</p>
                              <p className="font-semibold text-stone-900">
                                {ps.due_now_type === "dp" && `DP ${formatPrice(ps.due_now_amount || 0)}`}
                                {ps.due_now_type === "settlement" && `Pelunasan ${formatPrice(ps.due_now_amount || 0)}`}
                                {ps.due_now_type === "full" && `Tagihan ${formatPrice(ps.due_now_amount || 0)}`}
                                {!ps.due_now_type && `Menunggu Proses (${formatPrice(ps.outstanding_total || 0)})`}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-end">
                          <Link to={`/orders/${order.transaction_id}`} className="inline-flex items-center gap-2 bg-stone-900 text-white px-4 py-2 text-sm uppercase tracking-wider hover:bg-primary hover:text-primary-foreground transition-colors rounded-lg">
                            Lihat Order <ChevronRight className="w-4 h-4" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
